<template>
  <div class="audio-page-container">
    <div class="audio-container">
      <div class="overlay-container">
        <div :style="waveStyle" class="waveform-plot">
          <div :class="{ 'loading-waveform': isLoading1 }" ref="waveformContainer1"></div>
        </div>
        <div :style="specStyle">
          <!-- Wrap the spectrogram chart in a relative container -->
          <div class="spectrogram-wrapper">
            <!-- The spectrogram heatmap is rendered using Apache ECharts -->
            <v-chart v-if="chartOptions" :option="chartOptions" ref="spectrogramChart"
              style="width: 100%; min-height: 125px" @datazoom="handleSpectrogramDataZoom"
              @native:click="onSpectrogramClick($event)" autoresize />
            <!-- Overlay vertical line drawn at the pixel x-coordinate -->
            <div v-if="lineX !== null" class="vertical-line" :style="lineStyle"></div>
          </div>
        </div>
      </div>

      <div class="control-container">
        <div class="controls-inner">
          <div class="selector">
            <label for="audio-view">Audio view:</label>
            <select id="audio-view" v-model="view" @change="setView(view)">
              <option value="Waveform">Waveform</option>
              <option value="Spectrogram">Spectrogram</option>
            </select>
          </div>

          <div class="icon-container" @click="togglePlayPause(1)">
            <font-awesome-icon :icon="isPlaying1 ? 'fa-pause' : 'fa-play'" class="play-pause-icon" />
          </div>

          <span class="audio-time">{{ currentTime1 }}</span>

          <div class="volume-icon-container" @click="toggleMute">
            <font-awesome-icon :icon="isMuted ? 'fa-solid fa-volume-mute' : 'fa-solid fa-volume-high'
              " :class="{ 'mute-icon': isMuted, 'play-pause-icon': !isMuted }" />
          </div>

          <input type="range" min="0" max="1" step="0.001" v-model="volume" @input="adjustVolume" />

          <!-- Data view selector -->
          <div class="selector">
            <label for="data-view">Data view:</label>
            <select id="data-view" v-model="chartView" @change="setChartView(chartView)">
              <option value="Timeseries">Timeseries</option>
              <option value="Spectra">Spectra</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Region controls and source input -->
      <div class="region-controls" v-if="selectedRegion">
        <div class="region-info">
          <span>Selected Region: </span>
          <span>{{ formatDateTime(selectedRegion.start) }} -
            {{ formatDateTime(selectedRegion.end) }}</span>
          <div class="sources-list">
            <div v-for="(source, index) in selectedRegion.sources" :key="index" class="source-item">
              <span>{{ source }}</span>
              <button @click="removeSource(index)">×</button>
            </div>
          </div>
          <select v-model="newSource">
            <option disabled value="">Select a source</option>
            <option v-for="source in valid_sources" :key="source" :value="source">
              {{ source }}
            </option>
          </select>
          <button @click="addSource">Add Source</button>
          <button @click="deleteRegion">Delete Region</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { api_config } from "@/env.ts";
import axios from 'axios';
import { getToken } from '../keycloak-wrapper';
import { ref } from 'vue'
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import WaveSurfer from 'wavesurfer.js';

// Import Vue ECharts component.
import VChart from "vue-echarts";
// Import and register necessary ECharts modules.
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { HeatmapChart } from "echarts/charts";
import { VisualMapComponent, DataZoomComponent } from "echarts/components";

use([CanvasRenderer, HeatmapChart, VisualMapComponent, DataZoomComponent]);

export default {
  components: {
    VChart
  },
  props: {
    APIparams: {
      type: Object,
      default: () => ({})
    },
    // New prop for shared zoom, using v-model (two–way binding)
    sharedZoom: {
      type: Object,
      default: () => null
    },
    audioTime: {
      type: Number,
      default: null,
    },
    visible: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      waveSurfer1: null,
      regions: null,
      selectedRegion: null,
      audioURL: '',
      isPlaying1: false,
      isLoading1: true,
      currentTime1: '00:00',
      timeUpdateInterval: null,
      localDateTime: null,
      volume: 0.05, // default volume
      previousVolume: 0.05,
      isMuted: false,
      newSource: '',
      regionColors: ['rgba(255, 0, 0, 0.3)', 'rgba(0, 255, 0, 0.3)', 'rgba(0, 0, 255, 0.3)'],
      valid_sources: ["Mine", "Insects", "Birds", "Excavator", "Vehicle", "Wind", "Impact"],
      view: 'Waveform',
      chartView: 'Timeseries',
      chartOptions: null, // Options for the ECharts heatmap
      lineX: null      // Pixel x-coordinate for the vertical line overlay
    }
  },
  computed: {
    // Compute the inline style for the vertical line based on the pixel location.
    lineStyle() {
      return {
        position: 'absolute',
        top: '0px',
        left: this.lineX + 'px',
        height: '105px',
        borderLeft: '2px dashed #009B69',
        pointerEvents: 'none',
        zIndex: 10
      }
    },
    waveStyle() {
      if (!this.visible) {
        return {
          visibility: 'hidden',
        }
      }
      return {
        visibility: this.view === 'Waveform' ? 'visible' : 'hidden',
      }
    },
    specStyle() {
      if (!this.visible) {
        return {
          visibility: 'hidden',
        }
      }
      return {
        visibility: this.view === 'Spectrogram' ? 'visible' : 'hidden',
      }
    },
  },
  mounted() {
    this.initializeWaveform();
    this.loadSpectrogram();
  },
  setup() {
    const spectrogramChart = ref(null);
    return {
      spectrogramChart,
    };
  },
  methods: {
    setView(viewOption) {
      this.view = viewOption;
    },
    setChartView(viewOption) {
      // Allowed: 'Timeseries' | 'Spectra' | 'MLContributions'
      this.chartView = viewOption;
      this.$emit('update:chartView', viewOption);
    },
    async initializeWaveform() {
      try {
        this.currentTime1 = this.formatTime(0, 0);
        this.isLoading1 = true;

        await this.fetchAndCacheAudio();

        // Create the WaveSurfer instance with the Regions plugin.
        this.waveSurfer1 = WaveSurfer.create({
          container: this.$refs.waveformContainer1,
          waveColor: '#7C7C7C',
          progressColor: '#10b981',
          backend: 'MediaElement',
          cursorColor: 'black',
          cursorWidth: 2,
          fillParent: true,
          dragSelection: true,
          autoCenter: false,
          // scrollParent: false,
          plugins: [
            RegionsPlugin.create({
              color: 'rgba(255, 0, 0, 0.1)'
            })
          ],
          height: 125,
        });

        // Load the audio into WaveSurfer.
        this.waveSurfer1.load(this.audioURL);

        // Register event listeners.
        this.waveSurfer1.on('ready', () => {
          this.interval1 = setInterval(() => {
            this.updateCurrentTime(1);
          }, 100);
          this.isLoading1 = false;
          this.waveSurfer1.on('play', () => this.requestUpdate(1));
          this.waveSurfer1.on('pause', () => this.cancelUpdate(1));
          this.waveSurfer1.on('click', () => this.updateCurrentTime(1));
        });

        // Handle regions events.
        this.waveSurfer1.on('region-created', (region) => {
          console.log('Created region', region);
          region.setOptions({ color: this.randomColor() });
          this.selectedRegion = region;
        });
        this.waveSurfer1.on('region-updated', (region) => {
          console.log('Updated region', region);
          this.selectedRegion = region;
        });
        this.waveSurfer1.on('region-in', (region) => {
          console.log('Region in', region);
          this.selectedRegion = region;
        });

        this.updateVolume();
      } catch (error) {
        console.error('Error fetching audio:', error);
      }
    },

    async loadSpectrogram() {
      try {
        const token = await getToken();
        const response = await axios.post(
          api_config.backend + '/audioSpectrogramHeatmap',
          this.APIparams,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        // Expected response: { spectrogram: [[...]], time_axis: [...], frequency_axis: [...] }
        const spectrogramData = response.data.spectrogram;
        const timeAxis = response.data.time_axis;
        const frequencyAxis = response.data.frequency_axis;

        const xData = timeAxis;
        const yData = frequencyAxis.map(f => parseInt(f).toString());

        const yDataNear1000MultiplesMap = new Map();
        yData.forEach(val => {
          const closestMultiple = Math.round(val / 1000) * 1000;

          if (!yDataNear1000MultiplesMap.has(closestMultiple) ||
            Math.abs(val - closestMultiple) < Math.abs(yDataNear1000MultiplesMap.get(closestMultiple) - closestMultiple)) {
            yDataNear1000MultiplesMap.set(closestMultiple, val);
          }
        });
        const yDataNear1000Multiples = [...yDataNear1000MultiplesMap.values()];

        const formattedXData = xData.map(ts => {
          let date = new Date(ts);

          // Round to nearest second using milliseconds
          if (date.getUTCMilliseconds() >= 500) {
            date = new Date(date.getTime() + (1000 - date.getUTCMilliseconds()));
          } else {
            date = new Date(date.getTime() - date.getUTCMilliseconds());
          }

          return `${String(date.getUTCHours()).padStart(2, '0')}:` +
            `${String(date.getUTCMinutes()).padStart(2, '0')}:` +
            `${String(date.getUTCSeconds()).padStart(2, '0')}`;
        });

        const heatmapData = [];
        for (let f = 0; f < spectrogramData.length; f++) {
          for (let t = 0; t < spectrogramData[f].length; t++) {
            heatmapData.push([t, f, spectrogramData[f][t]]);
          }
        }

        let minValue = Infinity;
        let maxValue = -Infinity;
        for (let i = 0; i < heatmapData.length; i++) {
          const value = heatmapData[i][2];
          if (value < minValue) minValue = value;
          if (value > maxValue) maxValue = value;
        }

        const timeAxisLength = xData.length;
        const totalDuration = 15 * 60; // in seconds
        const currentPlaybackTime = this.currentPlaybackTime || 0;
        const initialPlaybackIndex = Math.floor((currentPlaybackTime * timeAxisLength) / totalDuration);
        this.lineX = initialPlaybackIndex + 50; // 50 for grid left offset

        this.chartOptions = {
          animation: false,
          tooltip: {
            formatter: function (params) {
              const timeValue = formattedXData[params.data[0]];
              const freqValue = yData[params.data[1]];
              return `Time: ${timeValue}<br/>Frequency: ${freqValue} Hz<br/>dB: ${params.data[2]?.toFixed(1)}`;
            }
          },
          xAxis: {
            type: 'category',
            data: formattedXData,
            axisLabel: {
              formatter: function (value) {
                return value;
              }
            },
            axisTick: {
              alignWithLabel: true,
              show: false,
            },
            // For category axis we will later update min/max based on sharedZoom
          },
          yAxis: {
            type: 'category',
            interval: 1000,
            data: yData,
            boundaryGap: true,
            axisLabel: {
              customValues: yDataNear1000Multiples,
              formatter: function (value) {
                const frequency = parseInt(value);
                const closestMultiple = Math.round(frequency / 1000) * 1000;
                return closestMultiple;
              }
            },
          },
          visualMap: {
            name: 'dBA',
            min: minValue,
            max: maxValue,
            orient: 'vertical',
            top: '-5px',
            right: '10px',
            inRange: {
              color: [
                "#000004",
                "#1b0c41",
                "#4a0c6b",
                "#781c6d",
                "#a52c60",
                "#cf4446",
                "#ed6925",
                "#fb9a06",
                "#f7d13d",
                "#fcffa4"
              ]
            }
          },
          // Add dataZoom controls for synchronization.
          dataZoom: [
            // { type: 'inside', xAxisIndex: 0, start: 0, end: 100 },
            // { type: 'inside', yAxisIndex: 0, start: 0, end: 100 },
            {
              id: 'xZoom',
              type: 'slider',
              xAxisIndex: 0,
              start: 0,
              end: 100,
              show: false,
            },
            {
              id: 'yZoom',
              type: 'slider',
              yAxisIndex: 0,
              filterMode: 'none',
              left: 10,
              top: 0,
              bottom: 20,
              width: 30,
              show: true
            },
          ],
          series: [{
            name: 'Spectrogram',
            type: 'heatmap',
            data: heatmapData,
            // renderAsImage: true,
            progressive: 400,
            animation: false,
            // emphasis: {
            //   itemStyle: {
            //     shadowBlur: 10,
            //     shadowColor: 'rgba(0, 0, 0, 0.5)'
            //   }
            // },
          }],
          grid: {
            left: 50,
            top: 0,
            right: 50,
            bottom: 20,
          },
        };

      } catch (error) {
        console.error("Error loading spectrogram heatmap:", error);
      }
    },

    async onSpectrogramClick(params) {
      // Update the overlay's x-coordinate using the click event's offset
      const pointInPixel = [params.offsetX, params.offsetY];
      const clickedTimeIndex = this.spectrogramChart.convertFromPixel({ seriesIndex: 0 }, pointInPixel)[0];

      const timeAxisLength = this.chartOptions.xAxis.data.length;
      if (isNaN(this.localDateTime)) {
        console.error("Invalid base date_time:", this.localDateTime);
        return;
      }

      if (clickedTimeIndex < 0 || clickedTimeIndex > timeAxisLength) {
        return;
      }

      if (!this.waveSurfer1 || !this.waveSurfer1.getDuration()) return;
      const audioDurationSeconds = this.waveSurfer1.getDuration();
      const clickedTimeAbs = this.localDateTime.getTime() * 1000 + (clickedTimeIndex * audioDurationSeconds * 1000 / timeAxisLength);
      const clickedTimeRelative = (clickedTimeAbs - this.localDateTime.getTime() * 1000) / 1000;
      if (isNaN(clickedTimeRelative) || clickedTimeRelative < 0) {
        console.error("Invalid clicked time:", clickedTimeRelative);
        return;
      }
      this.seekToTime(Math.round(clickedTimeRelative));
    },

    async smoothSeekTo(targetSeconds, duration = 500) {
      if (!this.waveSurfer1 || !this.waveSurfer1.getDuration()) return;
      const startSeconds = this.waveSurfer1.getCurrentTime();
      const delta = targetSeconds - startSeconds;
      const startTime = performance.now();
      const easeInCubic = (t) => Math.pow(t, 3);
      const animate = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1);
        const easedProgress = easeInCubic(t);
        const currentSeconds = startSeconds + delta * easedProgress;
        const ratio = Math.min(Math.max(currentSeconds / this.waveSurfer1.getDuration(), 0), 1);
        this.waveSurfer1.seekTo(ratio);

        if (t < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    },

    configureWaveSurferFrame() {

      if (!this.waveSurfer1 || !this.waveSurfer1.getDuration() || this.isLoading1 || !this.localDateTime) {
        return;
      }
      const scrollTimeInSeconds = (this.sharedZoom.minTime / 1000 - this.localDateTime.getTime() / 1000)
      this.waveSurfer1.setScrollTime(scrollTimeInSeconds);

      const frameDurationSeconds = (this.sharedZoom.maxTime - this.sharedZoom.minTime) / 1000;
      // Get the container width (assumes you have a ref="waveform" in your template)
      const containerWidth = this.$refs.waveformContainer1.clientWidth;
      // Calculate the zoom level in pixels per second so the frame fits the container width.
      // (If frameDuration is short, zoom is high; if long, zoom is lower.)
      const pixelsPerSecond = containerWidth / frameDurationSeconds;
      // Apply the zoom to Wavesurfer
      this.waveSurfer1.zoom(pixelsPerSecond);
      this.waveSurfer1.seekTo(scrollTimeInSeconds / this.waveSurfer1.getDuration()); // note seek to is required to correct the scroll time, and another seekTo is called later.

    },

    async seekToTime(seconds) {
      // this.smoothSeekTo(seconds, 350);
      this.currentTime1 = this.formatTime(this.localDatetime, seconds);

      // Seek to the desired playback time.
      // Note: The seek value is relative to the whole audio duration,
      // so even if the currentTime is beyond the frame’s midpoint, the progress is set exactly.
      if (this.waveSurfer1 && this.waveSurfer1.getDuration()) {
        const totalDuration = this.waveSurfer1.getDuration();
        this.configureWaveSurferFrame();
        const ratio = seconds / totalDuration;
        this.waveSurfer1.seekTo(Number(ratio));
      }


      /// update spec
      if (!this.spectrogramChart) {
        return;
      }
      const startSeconds = seconds;
      const chartDom = this.spectrogramChart.getDom();
      const chartWidth = chartDom.clientWidth - 100; // 100 px for left/right margins
      const durationSpec = (this.sharedZoom.maxTime - this.sharedZoom.minTime) / 1000;
      const secondsPassedMinTime = startSeconds - (this.sharedZoom.minTime / 1000 - this.localDateTime.getTime());
      const specRatio = Math.min(Math.max(secondsPassedMinTime / durationSpec, 0), 1);// seconds passed minTime / seconds in range min-max time
      this.lineX = 50 + specRatio * chartWidth; // 50 px for left

      this.updateCurrentTime(1);
    },

    handleAudioProgress(currentTime) { // note currentTime in seconds
      const sharedZoomMinTimeMs = this.sharedZoom.minTime;
      const sharedZoomMaxTimeMs = this.sharedZoom.maxTime;
      const currentTimeMs = currentTime * 1000;
      const baseTimeMs = this.localDateTime.getTime();
      const audioDurationMs = this.waveSurfer1.getDuration() * 1000;
      const minTimeMs = (sharedZoomMinTimeMs - baseTimeMs); // in ms
      const maxTimeMs = (sharedZoomMaxTimeMs - baseTimeMs);

      // If the playhead goes past the end of the current frame,
      // shift the frame so that the current time becomes the new maxTime.
      if (currentTimeMs > maxTimeMs) {
        const zoomDurationMs = maxTimeMs - minTimeMs;
        let newMaxTimeMs, newMinTimeMs;
        if (maxTimeMs == audioDurationMs) {
          return;
        }
        if (maxTimeMs + zoomDurationMs >= audioDurationMs) {
          newMaxTimeMs = audioDurationMs + baseTimeMs;
          newMinTimeMs = newMaxTimeMs - zoomDurationMs;
        }
        else {
          newMaxTimeMs = maxTimeMs + zoomDurationMs + baseTimeMs;
          newMinTimeMs = minTimeMs + zoomDurationMs + baseTimeMs;
        }
        const newMaxIndex = ((newMaxTimeMs - baseTimeMs) / audioDurationMs) * 100;
        const newMinIndex = ((newMinTimeMs - baseTimeMs) / audioDurationMs) * 100;
        if (newMinTimeMs != this.sharedZoom.minTime || newMaxTimeMs != this.sharedZoom.maxTime) {
          this.$emit('update:sharedZoom', { minIndex: newMinIndex, maxIndex: newMaxIndex, minTime: newMinTimeMs, maxTime: newMaxTimeMs });
          this.configureWaveSurferFrame();
        }
      }
    },

    requestUpdate(waveSurferNumber) {
      this.updateCurrentTime(waveSurferNumber);
    },

    random(min, max) {
      return Math.random() * (max - min) + min;
    },

    randomColor() {
      return `rgba(${this.random(0, 255)}, ${this.random(0, 255)}, ${this.random(0, 255)}, 0.5)`;
    },

    // DataZoom handler for spectrogram – converts the zoomed indices back to an absolute time range and emits the update.
    async handleSpectrogramDataZoom(e) {
      // Check if the event payload exists and has the expected structure.
      if (!e || !this.chartOptions.xAxis.data) {
        return;
      }

      // ECharts fires for all dataZooms; only act on the x-axis one.
      const batches = Array.isArray(e.batch) ? e.batch : [e];
      const xBatch = batches.find(b => b.dataZoomId === 'xZoom');
      if (!xBatch) return; // Ignore y-axis zoom events

      // const newMinIndex = e.start;
      // const newMaxIndex = e.end;

      // Convert category index to an absolute timestamp.
      // const baseTime = new Date(this.APIparams.date_time.split("Z")[0]).getTime();
      // const totalDuration = 15 * 60 * 1000; // 15 minutes in ms
      // const startFraction = newMinIndex / 100; // heatmaps use range of 0-100
      // const endFraction = newMaxIndex / 100;
      // const newMinTime = baseTime + Math.round(totalDuration * startFraction);
      // const newMaxTime = baseTime + Math.round(totalDuration * endFraction);


      // Emit the updated zoom range to the parent (using v-model binding)
      // requestAnimationFrame(() => {
      //   this.$emit('update:sharedZoom', { minIndex: newMinIndex, maxIndex: newMaxIndex, minTime: newMinTime, maxTime: newMaxTime });
      // });
    },

    async fetchAndCacheAudio() {
      const url = api_config.backend + '/audioPlaybackFirst';
      const token = await getToken();
      const networkResponse = await axios.post(
        url,
        this.APIparams,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          responseType: 'arraybuffer'
        }
      );
      const blob = new Blob([networkResponse.data], { type: 'audio/mpeg' });
      this.audioURL = URL.createObjectURL(blob);

      // 1) try to pull filename from the Content-Disposition header
      const contentDisp = networkResponse.headers['content-disposition'] || '';
      let filename;
      const headerMatch = contentDisp.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i);
      if (headerMatch) {
        filename = headerMatch[1];
      } else {
        console.warn('No filename available from server or params.');
      }

      // 2) extract the date/time from the filename
      if (filename) {
        const fnMatch = filename.match(/audiodata-(\d{8})-(\d{6})\.(?:mp3|wav|.+)$/);
        if (fnMatch) {
          const [, dateStr, timeStr] = fnMatch;
          const iso =
            `${dateStr.substr(0, 4)}-${dateStr.substr(4, 2)}-${dateStr.substr(6, 2)}` +
            `T${timeStr.substr(0, 2)}:${timeStr.substr(2, 2)}:${timeStr.substr(4, 2)}`;
          this.localDateTime = new Date(iso);
        } else {
          console.warn('Filename did not match expected timestamp pattern:', filename);
          this.localDateTime = null;
        }
      } else {
        console.warn('No audio filename in content-disposition', contentDisp);
        this.localDateTime = null;
      }
      return blob;
    },

    cancelUpdate(waveSurferNumber) {
      if (waveSurferNumber === 1) {
        cancelAnimationFrame(this.frameRequest1);
      } else if (waveSurferNumber === 2) {
        cancelAnimationFrame(this.frameRequest2);
      }
    },

    updateCurrentTime(waveSurferNumber) {
      if (waveSurferNumber === 1) {
        if (this.waveSurfer1.getCurrentTime()) {
          this.currentTime1 = this.formatTime(this.waveSurfer1.getCurrentTime(), 0);
        } else {
          this.currentTime1 = this.formatTime(0, 0);
        }
        // If spectrogram view is active, update the overlay line position.
        if (this.view === 'Spectrogram' && this.chartOptions && this.spectrogramChart) {
          const startSeconds = this.waveSurfer1.getCurrentTime();
          const chartDom = this.spectrogramChart.getDom();
          const chartWidth = chartDom.clientWidth - 100; // 100 px for left/right margins
          const durationSpec = (this.sharedZoom.maxTime - this.sharedZoom.minTime) / 1000;
          const secondsPassedMinTime = startSeconds - (this.sharedZoom.minTime - this.localDateTime.getTime()) / 1000;
          const specRatio = Math.min(Math.max(secondsPassedMinTime / durationSpec, 0), 1);// seconds passed minTime / seconds in range min-max time
          this.lineX = 50 + specRatio * chartWidth; // 50 px for left
        }
        this.handleAudioProgress(this.waveSurfer1.getCurrentTime());

        let newTime = Math.trunc(this.localDateTime.getTime() + this.waveSurfer1.getCurrentTime() * 1000);
        const remainder = newTime % 1000; // update each second
        newTime = newTime - remainder;
        if (newTime != this.audioTime) {
          this.$emit('update:audioTime', newTime);
        }

        // this.frameRequest1 = requestAnimationFrame(() => this.updateCurrentTime(1));
      }
      if (waveSurferNumber === 2 && this.waveSurfer2) {
        this.currentTime2 = this.formatTime(this.waveSurfer2.getCurrentTime(), 15 * 60);
        requestAnimationFrame(() => this.updateCurrentTime(2));
      }
    },

    togglePlayPause(audioNumber) {
      const waveSurfer = audioNumber === 1 ? this.waveSurfer1 : this.waveSurfer2;
      const isPlaying = audioNumber === 1 ? 'isPlaying1' : 'isPlaying2';
      if (waveSurfer) {
        waveSurfer.playPause();
        this[isPlaying] = waveSurfer.isPlaying();
      }
    },

    addSeconds(date, seconds) {
      return new Date(date.getTime() + seconds * 1000);
    },

    formatTime(seconds, offset) {
      if (!this.waveSurfer1 || !this.waveSurfer1.getDuration() || !this.localDateTime) return
      const startTime = this.addSeconds(this.localDateTime, offset); //.toISOString().slice(0, 19).replace('T', ' ')
      const currentTime = this.addSeconds(startTime, seconds);
      const finalTime = this.addSeconds(startTime, this.waveSurfer1.getDuration());
      const currentHour = String(currentTime.getHours()).padStart(2, '0');
      const currentMinutes = String(currentTime.getMinutes()).padStart(2, '0');
      const currentSeconds = String(currentTime.getSeconds()).padStart(2, '0');
      const finalHour = String(finalTime.getHours()).padStart(2, '0');
      const finalMinutes = String(finalTime.getMinutes()).padStart(2, '0');
      const finalSeconds = String(finalTime.getSeconds()).padStart(2, '0');
      return `${currentHour}:${currentMinutes}:${currentSeconds} / ${finalHour}:${finalMinutes}:${finalSeconds}`;
    },

    toggleMute() {
      if (!this.isMuted) {
        this.previousVolume = this.volume;
        this.volume = 0;
      } else {
        this.volume = this.previousVolume;
      }
      this.isMuted = !this.isMuted;
      this.updateVolume();
    },

    adjustVolume() {
      if (this.volume > 0 && this.isMuted) {
        this.isMuted = false;
      }
      this.updateVolume();
    },

    async updateVolume() {
      const newVolume = this.isMuted ? 0 : this.volume;
      if (this.waveSurfer1) {
        this.waveSurfer1.setVolume(newVolume);
      }
      if (this.waveSurfer2) {
        this.waveSurfer2.setVolume(newVolume);
      }
    },
  },
  watch: {
    // Watch for changes in the sharedZoom prop and update the spectrogram and waveform accordingly.
    sharedZoom(newZoom) {
      if (newZoom && this.chartOptions) {
        // For a category axis, update the min and max indices.
        // this.chartOptions.xAxis.min = startIndex;
        // this.chartOptions.xAxis.max = endIndex;
        if (this.spectrogramChart) {
          // this.chartOptions.dataZoom[0].start = newZoom.minIndex;
          // this.chartOptions.dataZoom[0].end = newZoom.maxIndex;
          this.spectrogramChart.setOption({
            dataZoom: [{
              start: Math.round(newZoom.minIndex),
              end: Math.round(newZoom.maxIndex),
              show: false,
            }]
          }, { notMerge: false }); // or false if you want to preserve other options
        }
      }
      if (newZoom && this.waveSurfer1) { // && this.waveSurfer1.zoom
        const currentTimeSeconds = this.waveSurfer1.getCurrentTime();
        let timeToSeekSeconds = currentTimeSeconds;
        if (currentTimeSeconds > (newZoom.maxTime - this.localDateTime.getTime()) / 1000
          || currentTimeSeconds < (newZoom.minTime - this.localDateTime.getTime()) / 1000) {
          timeToSeekSeconds = (newZoom.minTime - this.localDateTime.getTime()) / 1000;
        }
        this.seekToTime(timeToSeekSeconds);
      }
    },
    // 'waveSurfer1.interval1': function (newVal) {
    // if (newVal) {
    //   this.interval1 = setInterval(() => {
    //     this.updateCurrentTime();
    //   }, 200);
    // } else {
    //   clearInterval(this.interval1);
    // }
    // },
  },
  beforeUnmount() {
    if (this.waveSurfer1) {
      this.waveSurfer1.destroy();
      clearInterval(this.interval1);
    }
    if (this.waveSurfer2) {
      this.waveSurfer2.destroy();
    }
    cancelAnimationFrame(this.frameRequest1);
    clearInterval(this.timeUpdateInterval);

    // Revoke the blob URL for the audio to free memory.
    if (this.audioURL) {
      URL.revokeObjectURL(this.audioURL);
    }
  }
}
</script>

<style scoped>
/* (Styles remain largely unchanged) */
.audio-container {
  width: 100%;
  background-color: var(--colour-bg-light);
}

.spectrogram-wrapper {
  position: relative;
}

.overlay-container {
  position: relative;
  width: 100%;
  height: 7.8125rem;
}

.audio-page-container {
  width: 100%;
}

.play-pause-icon {
  cursor: pointer;
  font-size: var(--font-size-lg);
  color: var(--color-secondary);
}

.icon-container {
  background-color: var(--colour-bg-light);
  border-radius: var(--radius-md);
  width: var(--control-height-md);
  height: var(--control-height-md);
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid #777;
  margin: 0;
  cursor: pointer;
}

.control-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-xs);
  margin-right: var(--spacing-2xl);
  margin-left: var(--spacing-2xl);
  background-color: var(--colour-bg-muted);
  border: 2px solid #bbb;
  border-bottom-left-radius: var(--radius-lg);
  border-bottom-right-radius: var(--radius-lg);
}

.controls-inner {
  display: grid;
  grid-template-columns: 3fr 1fr 3fr 1fr 10fr 3fr;
  align-items: center;
  gap: var(--spacing-ml);
  width: 100%;
}

.view-selector {
  font-size: var(--font-size-md);
}

.view-selector span {
  cursor: pointer;
  color: var(--colour-border-strong);
}

.view-selector span.active {
  font-weight: bold;
  color: #000;
}

.selector {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
}

.selector select {
  padding: 4px 6px;
  border: 1px solid var(--colour-border);
  border-radius: 6px;
  background: var(--colour-surface);
}

.selector label {
  font-size: var(--font-size-sm);
  color: var(--colour-text-dark);
}

.chart-selector {
  font-size: var(--font-size-md);
}

.chart-selector span {
  cursor: pointer;
  color: var(--colour-border-strong);
}

.chart-selector span.active {
  font-weight: bold;
  color: #000;
}

.audio-time {
  margin-left: 0px;
  font-size: var(--font-size-md);
  color: #000;
}

@keyframes skeleton-loading {
  0% {
    background-color: hsl(200, 20%, 80%);
  }

  100% {
    background-color: hsl(200, 20%, 95%);
  }
}

.loading-waveform {
  animation: skeleton-loading 1s linear infinite alternate;
}

.volume-icon-container {
  background-color: var(--colour-bg-light);
  border-radius: var(--radius-md);
  width: 2.5rem;
  height: var(--control-height-md);
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid #777;
  cursor: pointer;
}

.mute-icon {
  color: red;
  font-size: var(--font-size-lg);
  cursor: pointer;
}

.waveform-plot {
  position: absolute;
  background-color: var(--colour-bg-light);
  width: 100%;
  height: 7.8125rem;
  margin: 0;
  border-bottom: 0;
  padding-left: var(--spacing-2xl);
  padding-right: var(--spacing-2xl);
  overflow: hidden;
}

.spectrogram-plot {
  position: absolute;
  background-color: #000;
  width: 100%;
  height: 7.8125rem;
  margin: 0;
  border-bottom: 0;
}

/* Additional styles for region controls and export buttons */
.region-controls {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-ml);
  background: var(--colour-bg-subtle);
  border-radius: var(--radius-md);
}

.source-item {
  display: flex;
  justify-content: space-between;
  margin: var(--spacing-xs) 0;
  padding: var(--spacing-xs);
  background: var(--colour-surface);
  border-radius: var(--radius-sm);
}

.export-button {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--color-secondary);
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}
</style>
